import React, { useState, useContext } from "react";
import { AuthContext } from "../App";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { CheckCircle, ArrowRight, ArrowLeft, Trophy } from "lucide-react";

const SkillTestTake = () => {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  
  const { testId, skillName, questions } = location.state || {};
  
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  if (!testId || !questions) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="container mx-auto px-4 py-8">
          <Card>
            <CardContent className="pt-6 text-center">
              <p className="text-gray-600 mb-4">No test data found</p>
              <Button onClick={() => navigate("/skill-tests")}>
                Back to Skill Tests
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const currentQ = questions[currentQuestion];
  const progress = ((currentQuestion + 1) / questions.length) * 100;

  const handleAnswer = (questionId, answer) => {
    setAnswers({ ...answers, [questionId]: answer });
  };

  const handleNext = () => {
    if (!answers[currentQ.id]) {
      toast.error("Please select an answer");
      return;
    }
    
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const handleSubmit = async () => {
    if (!answers[currentQ.id]) {
      toast.error("Please answer the current question");
      return;
    }

    if (Object.keys(answers).length < questions.length) {
      toast.error("Please answer all questions before submitting");
      return;
    }

    try {
      setSubmitting(true);
      const response = await axios.post(
        `${API}/skills/submit-test`,
        {
          test_id: testId,
          skill_name: skillName,
          answers: answers
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setResult(response.data);
      toast.success("Test submitted successfully!");
    } catch (error) {
      toast.error("Failed to submit test");
    } finally {
      setSubmitting(false);
    }
  };

  // Results Screen
  if (result) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-purple-50">
        <Navbar />
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-8">
              <div className="inline-block p-4 bg-green-100 rounded-full mb-4">
                <Trophy className="w-12 h-12 text-green-600" />
              </div>
              <h1 className="text-4xl font-bold mb-2">Test Complete!</h1>
              <p className="text-gray-600">Here are your results for {skillName}</p>
            </div>

            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="text-center text-3xl">
                  Score: {result.score}%
                </CardTitle>
                <CardDescription className="text-center">
                  {result.correct_answers} out of {result.total_questions} correct
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Progress value={result.score} className="h-4 mb-4" />
                
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <p className="font-semibold text-blue-900 mb-1">Feedback</p>
                  <p className="text-blue-800">{result.feedback}</p>
                </div>

                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                  <p className="font-semibold text-indigo-900 mb-1">Points Earned</p>
                  <p className="text-2xl font-bold text-indigo-600">+{result.points_earned} points</p>
                </div>
              </CardContent>
            </Card>

            <div className="flex gap-4 justify-center">
              <Button onClick={() => navigate("/skill-tests")} variant="outline">
                Back to Skill Tests
              </Button>
              <Button onClick={() => navigate("/skill-test/gap-analysis")}>
                View Skill Gap Analysis
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Test Taking Screen
  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-2xl font-bold">{skillName} Test</h2>
              <Badge variant="outline">
                Question {currentQuestion + 1} of {questions.length}
              </Badge>
            </div>
            <Progress value={progress} className="h-2" />
          </div>

          {/* Question Card */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2 mb-2">
                <Badge variant={currentQ.type === "scenario" ? "default" : "secondary"}>
                  {currentQ.type === "scenario" ? "Scenario-Based" : "Multiple Choice"}
                </Badge>
              </div>
              <CardTitle className="text-xl">{currentQ.question}</CardTitle>
            </CardHeader>
            <CardContent>
              <RadioGroup
                value={answers[currentQ.id]}
                onValueChange={(value) => handleAnswer(currentQ.id, value)}
              >
                {currentQ.options.map((option, idx) => (
                  <div
                    key={idx}
                    className="flex items-center space-x-2 p-3 hover:bg-gray-50 rounded-lg border border-transparent hover:border-indigo-200"
                  >
                    <RadioGroupItem value={option} id={`${currentQ.id}-${idx}`} />
                    <Label htmlFor={`${currentQ.id}-${idx}`} className="flex-1 cursor-pointer">
                      {option}
                    </Label>
                  </div>
                ))}
              </RadioGroup>

              {/* Navigation */}
              <div className="flex gap-3 mt-6">
                {currentQuestion > 0 && (
                  <Button variant="outline" onClick={handlePrevious}>
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Previous
                  </Button>
                )}
                {currentQuestion < questions.length - 1 ? (
                  <Button onClick={handleNext} className="ml-auto">
                    Next
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                ) : (
                  <Button onClick={handleSubmit} disabled={submitting} className="ml-auto">
                    {submitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Submitting...
                      </>
                    ) : (
                      <>
                        Submit Test
                        <CheckCircle className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default SkillTestTake;
